import { ErrorState, LoadingState } from '@hemmafixa/ui';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import type { Area } from '@/app/navigation';
import { useI18n } from '@/i18n/context';
import { canUse, homePath } from './access';
import { useSession } from './api';
import { CurrentUserContext } from './current-user';

function FullPage({ children }: { children: ReactNode }) {
  return <main className="mx-auto grid min-h-dvh max-w-3xl content-center p-4">{children}</main>;
}

/**
 * Opens an area only for a signed-in user with that role. Logged out: to the login page, which comes back
 * here afterwards. Wrong role: to the user's own area. The API checks the same on every request.
 */
export function RequireArea({ area, children }: { area: Area; children: ReactNode }) {
  const { t } = useI18n();
  const location = useLocation();
  const session = useSession();

  if (session.isPending) {
    return (
      <FullPage>
        <LoadingState variant="cards" label={t.common.loading} />
      </FullPage>
    );
  }
  if (session.isError) {
    return (
      <FullPage>
        <ErrorState
          title={t.common.errorTitle}
          message={t.common.errorMessage}
          retryLabel={t.common.retry}
          onRetry={() => void session.refetch()}
        />
      </FullPage>
    );
  }
  if (!session.data) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  if (!canUse(session.data, area)) return <Navigate to={homePath(session.data)} replace />;
  return <CurrentUserContext.Provider value={session.data}>{children}</CurrentUserContext.Provider>;
}

/** `/`: the user's own area when logged in, otherwise the login page. */
export function StartRedirect() {
  const session = useSession();
  if (session.isPending) return null;
  return <Navigate to={session.data ? homePath(session.data) : '/login'} replace />;
}
